import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { get } from 'lodash';
import { Observable, catchError, throwError } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserEffects } from '../../state/user/user.effects';
import { IValidationError } from '../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../utilities/snackbar.service';
import { environment } from '../environments/environment';
import { SCREEN_LOCK_RETRIED, SCREEN_LOCK_UNLOCK_REQUIRED_STATUS } from '../services/screen-lock.interface';
import { VersionUpdateService } from '../services/version-update.service';
import { ApiInterceptorCacheService } from './api-interceptor-cache.service';

type TUrlTarget = 'digitalBoards' | 'oeeTracker' | 'actionTracker';
const fullUrlsWithCredentials = [
  'https://autopilot.scm3d.com',
  'https://df-sso.scm3d.com',
  'https://dev-file-manager.scm3d.com',
  'https://file-manager.scm3d.com',
  'https://test-file-manager.scm3d.com',
  'https://training-file-manager.scm3d.com',
  'https://uat-file-manager.scm3d.com',
];

export const API_TARGET = new HttpContextToken<TUrlTarget>(() => 'digitalBoards');
export const oeeContext = new HttpContext().set(API_TARGET, 'oeeTracker');
export const actionTrackerContext = new HttpContext().set(API_TARGET, 'actionTracker');
/** Opts a request out of the snackbar and the login/forbidden redirects this interceptor applies on an error. */
export const SKIP_ERROR_MESSAGE_AND_REDIRECT_HEADER = 'Skip-Error-Message-And-Redirect';
export const API_CACHE_MODE_HEADER = 'x-api-cache-mode';
export const API_CACHE_TTL_HEADER = 'x-api-cache-ttl';

const blockingErrorKeys: string[] = ['digitalBoardsAccessDenied'];

export enum EApiCacheMode {
  none = 1,
  inFlight = 2,
  cacheAndInFlight = 3,
}

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private isBlockingErrorVisible = false;

  constructor(
    private readonly snackBar: SnackbarService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly cacheService: ApiInterceptorCacheService,
    private readonly versionUpdateService: VersionUpdateService,
  ) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isFullUrl = (url: string): boolean => /^(http|https):\/\//.test(url);
    const isLocalAssets = (url: string): boolean => /\/assets\//.test(url);

    if (isFullUrl(req.url) || isLocalAssets(req.url)) {
      const withCredentials = fullUrlsWithCredentials.some((url) => req.url.startsWith(url));

      return next.handle(
        req.clone({
          withCredentials,
        }),
      );
    }

    const apiReq: HttpRequest<unknown> = ApiInterceptor.modifyRequest(req);

    const cacheMode = this.resolveCacheMode(apiReq);
    const cacheKey = this.cacheService.getCacheKey(apiReq);

    switch (cacheMode) {
      case EApiCacheMode.none:
        return this.handleRequest(apiReq, next);

      case EApiCacheMode.inFlight:
        return this.cacheService.getOrCreateInFlight(cacheKey, () => this.handleRequest(apiReq, next));

      case EApiCacheMode.cacheAndInFlight:
        const ttlMs = this.resolveTtlMs(apiReq);

        return this.cacheService.getOrCreate(cacheKey, () => this.handleRequest(apiReq, next), ttlMs);

      default:
        return this.handleRequest(apiReq, next);
    }
  }

  private handleRequest(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (!(error instanceof HttpErrorResponse) || req.headers.has(SKIP_ERROR_MESSAGE_AND_REDIRECT_HEADER)) {
          return throwError(() => error);
        }

        const target: TUrlTarget = req.context.get(API_TARGET);

        // `screenLockInterceptor` (registered outside this one) answers a 423 with the PIN keypad and replays
        // the write, so a snackbar here would say the same thing twice. A 423 on the replay is a genuine
        // failure and falls through to the normal handling below.
        if (
          error.status === SCREEN_LOCK_UNLOCK_REQUIRED_STATUS &&
          target === 'digitalBoards' &&
          !req.context.get(SCREEN_LOCK_RETRIED)
        ) {
          return throwError(() => error);
        }

        if (error.status === 428 && target === 'digitalBoards') {
          this.versionUpdateService.handleVersionMismatch();
        } else if (error.status === 401) {
          redirectToLogin(get(error, 'error.message.0.constraints.ssoClient'));
        } else {
          this.handleErrorMessage(error);
        }

        if (error.status === 403) {
          // `replaceUrl` keeps "Back" from returning to the route that just failed and triggering the same 403.
          const isBlocking: boolean = ApiInterceptor.getErrorKeys(error).some((key) => blockingErrorKeys.includes(key));

          this.router.navigate(['/error/forbidden'], { replaceUrl: isBlocking });
        }

        return throwError(() => error);
      }),
    );
  }

  private handleErrorMessage(err: HttpErrorResponse): void {
    const { message: error }: { message: IValidationError[] } = err.error;

    try {
      error.forEach((message: IValidationError) => {
        Object.keys(message.constraints).forEach((errorKey) => {
          this.translate
            .get(`apiError.${errorKey}`, this.additionalOptions(errorKey, message))
            .subscribe((translatedErrorMessage: string | undefined) => {
              const errorMessage: string =
                translatedErrorMessage !== undefined ? translatedErrorMessage : 'apiError.error';

              if (blockingErrorKeys.includes(errorKey)) {
                this.openBlockingError(errorMessage);

                return;
              }

              this.snackBar.open(errorMessage, ESnackbar.error);
            });
        });
      });
    } catch (_e) {
      this.snackBar.open(this.translate.instant('apiError.error'), ESnackbar.error);
    }
  }

  private openBlockingError(message: string): void {
    if (this.isBlockingErrorVisible) {
      return;
    }

    this.isBlockingErrorVisible = true;

    const snackBarRef = this.snackBar.open(message, ESnackbar.error, 0, this.translate.instant('button.back'));

    snackBarRef
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        this.isBlockingErrorVisible = false;
      });

    snackBarRef
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        window.history.back();
      });
  }

  private static getErrorKeys(err: HttpErrorResponse): string[] {
    const messages: IValidationError[] = get(err, 'error.message', []) as IValidationError[];

    return Array.isArray(messages) ? messages.flatMap((message) => Object.keys(message?.constraints ?? {})) : [];
  }

  private additionalOptions(errorKey: string, errorMessage: IValidationError): { [key: string]: string } {
    const additionalOptions = {};

    for (const [key, value] of Object.entries(_.get(errorMessage, `contexts.${errorKey}`, {}))) {
      const translateString = `apiErrorMessages.additionalOptions.${errorKey}.${key}`;
      const translatedResult = this.translate.instant(translateString);

      _.set(additionalOptions, key, translateString === translatedResult ? value : translatedResult);
    }

    return additionalOptions;
  }

  private static modifyRequest(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const target: TUrlTarget = req.context.get(API_TARGET);
    const apiUrl: string = getApiUrl(req.context.get(API_TARGET));
    let { headers } = req;

    if (UserEffects.getUser() && UserEffects.getUser().client) {
      headers = req.headers.set('x-dbo-client', UserEffects.getUser().client);
    }

    if (target === 'digitalBoards') {
      headers = headers.set('x-dbo-version', environment.version);
    }

    return req.clone({
      headers,
      url: `${apiUrl}/${req.url}`,
      withCredentials: true,
    });
  }

  private resolveCacheMode(req: HttpRequest<unknown>): EApiCacheMode {
    const headerValue = req.headers.get(API_CACHE_MODE_HEADER);
    let output: EApiCacheMode = EApiCacheMode.none;

    if (headerValue) {
      return Number(headerValue) as EApiCacheMode;
    }

    switch (req.method) {
      case 'GET':
        output = EApiCacheMode.cacheAndInFlight;
        break;
      case 'POST':
        output = EApiCacheMode.inFlight;
        break;
      default:
        output = EApiCacheMode.none;
    }

    return output;
  }

  private resolveTtlMs(req: HttpRequest<unknown>): number {
    const ttlHeader = req.headers.get(API_CACHE_TTL_HEADER);
    const ttlSeconds = ttlHeader ? Number(ttlHeader) : 2;

    return ttlSeconds * 1_000;
  }
}

export const getApiUrl = (target: TUrlTarget = 'digitalBoards'): string => {
  const { origin } = location;
  const possibleSearchValues = {
    'digital-boards': 'digital-boards-api',
  };

  if (target === 'oeeTracker') {
    return UserEffects.getUser().moduleUrls.oeeTrackerBackendUrl;
  }

  if (target === 'actionTracker') {
    return UserEffects.getUser().moduleUrls.actionTrackerBackendUrl;
  }

  const regExp = new RegExp(Object.keys(possibleSearchValues).join('|'), 'gi');

  let apiUrl = origin.replace(regExp, (match) => {
    return possibleSearchValues[match.toLowerCase() as keyof typeof possibleSearchValues];
  });

  if (!environment.production) {
    apiUrl = `${apiUrl}/api`;
  }

  return apiUrl;
};

function redirectToLogin(ssoClient?: string): boolean {
  let url = `${environment.ssoUrl + environment.ssoAuthUrl + environment.ssoEnv}?state=${encodeURIComponent(window.location.href)}`;

  if (ssoClient) {
    url += `&client=${ssoClient}`;
  }

  window.location.replace(url);

  return false;
}
